import { Injectable, signal, computed } from '@angular/core';
import { Delegation } from '../api/model/delegation';

/**
 * Holds the currently selected eDesk URI and the full delegation list for the session.
 */
@Injectable({ providedIn: 'root' })
export class EdeskStateService {
    private readonly STORAGE_KEY = 'selectedEdeskUri';

    readonly selectedEdeskUri = signal<string | null>(sessionStorage.getItem('selectedEdeskUri'));
    readonly delegations = signal<Delegation[]>([]);

    readonly selectedDelegation = computed(() =>
        this.delegations().find(d => d.edesk?.uri === this.selectedEdeskUri()) ?? null
    );

    readonly selectedEdeskLabel = computed(() => {
        const d = this.selectedDelegation();
        return d?.edesk?.label ?? d?.edesk?.uri ?? null;
    });

    /** Shorter label for display: uses label if set, otherwise strips the URI scheme prefix
     *  e.g. "rc://sk/390504420_blazeny_pankrac" → "390504420_blazeny_pankrac" */
    readonly selectedEdeskShortLabel = computed(() => {
        const d = this.selectedDelegation();
        if (d?.edesk?.label) return d.edesk.label;
        const uri = d?.edesk?.uri;
        if (!uri) return null;
        const lastSlash = uri.lastIndexOf('/');
        return lastSlash >= 0 ? uri.substring(lastSlash + 1) : uri;
    });

    setDelegations(delegations: Delegation[]): void {
        this.delegations.set(delegations);
    }

    setSelectedEdeskUri(uri: string): void {
        this.selectedEdeskUri.set(uri);
        sessionStorage.setItem(this.STORAGE_KEY, uri);
    }

    clearSelectedEdeskUri(): void {
        this.selectedEdeskUri.set(null);
        sessionStorage.removeItem(this.STORAGE_KEY);
    }
}
